/**
 * One group of Risk settings (identity screening / compliance gates / signing).
 * Edits are held as a draft; Save opens a confirm dialog (old → new + reason),
 * then each changed key is written with PUT /v3/risk/settings and the screen
 * reloads both the stored values and the effective read-back, so what it shows
 * afterwards is what the system will actually use.
 */

import { useMemo, useState } from 'react';

import { saveRiskSetting, type RiskSettingRow } from '@/services/riskSettingsApi';

import { ConfirmChangesDialog, type PendingChange } from './ConfirmChangesDialog';
import { SettingControl, displayValue, parseMatrix, validateDraft } from './controls';

interface Props {
  readonly title: string;
  readonly intro: string;
  readonly rows: ReadonlyArray<RiskSettingRow>;
  readonly effective: Readonly<Record<string, string>>;
  readonly onSaved: (message: string) => Promise<void>;
  readonly onError: (message: string) => void;
}

/** Multi-selects compare as sets in catalogue order, so "phone,dob" equals "dob,phone". */
function normalize(row: RiskSettingRow, v: string | null): string {
  const value = (v ?? '').trim();
  if (row.control === 'matrix') {
    // Compare matrices as their canonical form: every row, in catalogue order.
    const m = parseMatrix(value);
    const first = row.options?.[0]?.value ?? '';
    const full: Record<string, string> = {};
    for (const r of row.rows ?? []) full[r.value] = m[r.value] ?? first;
    return JSON.stringify(full);
  }
  if (row.control !== 'multi') return value;
  const parts = value.split(',').map((s) => s.trim()).filter(Boolean);
  return (row.options ?? []).map((o) => o.value).filter((o) => parts.includes(o)).join(',');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function SettingGroupCard({ title, intro, rows, effective, onSaved, onError }: Props): JSX.Element {
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const dirtyRows = useMemo(
    () => rows.filter((r) => r.settingKey in draft && normalize(r, draft[r.settingKey]) !== normalize(r, r.settingValue)),
    [rows, draft],
  );
  const invalid = dirtyRows.some((r) => validateDraft(r, draft[r.settingKey]) != null);

  const pending: PendingChange[] = dirtyRows.map((r) => ({
    id: r.settingKey,
    label: r.label ?? r.settingKey,
    oldText: displayValue(r, r.settingValue),
    newText: displayValue(r, normalize(r, draft[r.settingKey])),
  }));
  const takesEffect = Array.from(new Set(dirtyRows.map((r) => r.takesEffect).filter(Boolean))).join(' ');

  const save = async (reason: string): Promise<void> => {
    setBusy(true);
    const done: string[] = [];
    try {
      for (const r of dirtyRows) {
        await saveRiskSetting(r.settingKey, normalize(r, draft[r.settingKey]), reason);
        done.push(r.label ?? r.settingKey);
      }
      setDraft({});
      setConfirming(false);
      await onSaved(`Saved: ${done.join('; ')}. ${takesEffect}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed.';
      setConfirming(false);
      onError(done.length > 0
        ? `Saved ${done.join('; ')}, then stopped: ${msg} The other changes were not saved.`
        : `Nothing was saved: ${msg}`);
      setDraft({});
      await onSaved('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card rs-group">
      <div className="card-header"><h2>{title}</h2></div>
      <p className="rs-group__intro">{intro}</p>

      {rows.map((r) => {
        const value = r.settingKey in draft ? draft[r.settingKey] : (r.settingValue ?? '');
        const isDirty = dirtyRows.includes(r);
        const eff = effective[r.settingKey];
        const effDiffers = eff != null && normalize(r, eff) !== normalize(r, r.settingValue);
        return (
          <div key={r.settingKey} className={`rs-row${isDirty ? ' rs-row--dirty' : ''}`}>
            <div>
              <div className="rs-row__label">{r.label ?? r.settingKey}</div>
              {r.affects && <div className="rs-row__affects">{r.affects}</div>}
              <div className="rs-row__meta">
                <span>Default: <strong>{displayValue(r, r.defaultValue)}</strong></span>
                {r.usingDefault
                  ? <span className="pill pill--muted">Using default</span>
                  : <span>Last changed {formatWhen(r.updatedAt)}{r.updatedBy ? ` by ${r.updatedBy}` : ''}</span>}
                {eff != null && (
                  <span className={`pill ${effDiffers ? 'pill--warn' : 'pill--ok'}`} title="Read back from the code that uses this setting">
                    In use now: {displayValue(r, eff)}
                  </span>
                )}
              </div>
              {effDiffers && (
                <div className="rs-row__warn">
                  The system is using a different value from the one saved: the saved value is not one it recognises
                  (or, for the FraudPoint cut-offs, the pair does not make sense together, so 750 / 500 are in force).
                </div>
              )}
              {r.boundNote && <div className="rs-row__note">{r.boundNote}</div>}
              <details>
                <summary>Where this comes from</summary>
                <dl>
                  <dt>Source</dt><dd>{r.citation ?? r.description}</dd>
                  <dt>Takes effect</dt><dd>{r.takesEffect}</dd>
                  <dt>Used by</dt><dd className="rs-mono">{r.readBy}</dd>
                  <dt>Setting key</dt><dd className="rs-mono">{r.settingKey}</dd>
                </dl>
              </details>
            </div>
            <div>
              <SettingControl
                row={r}
                value={value}
                disabled={busy}
                onChange={(v) => setDraft((d) => ({ ...d, [r.settingKey]: v }))}
              />
            </div>
          </div>
        );
      })}

      <div className="rs-actions">
        <span className="rs-actions__count">
          {dirtyRows.length === 0 ? 'No unsaved changes' : `${dirtyRows.length} unsaved change${dirtyRows.length === 1 ? '' : 's'}`}
        </span>
        <button type="button" className="btn btn--ghost" disabled={busy || dirtyRows.length === 0} onClick={() => setDraft({})}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" disabled={busy || dirtyRows.length === 0 || invalid}
          onClick={() => setConfirming(true)}>
          Save changes
        </button>
      </div>

      {confirming && (
        <ConfirmChangesDialog
          title={`Save changes to ${title.toLowerCase()}?`}
          changes={pending}
          takesEffect={takesEffect}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={(reason) => void save(reason)}
        />
      )}
    </section>
  );
}
